import { Alert, Select, Skeleton, Stack, Text, Title } from "@mantine/core";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useActiveBatchId, useBatch, useBatches } from "../api/hooks";
import type { BatchDetail, BatchSummary } from "../api/types";
import { periodLabelFrom } from "../lib/datetime";

/** Dropdown label for a batch option, e.g. "March 2026 · Open". */
function optionLabel(b: BatchSummary): string {
  const period = b.period_label ?? periodLabelFrom(b.period_start);
  return `${period} · ${b.state}`;
}

/**
 * Scaffold for the "global" pages (Issues / Reports / Audit) that each render a
 * single batch's per-batch tab. Lets the user pick which batch via a dropdown
 * of all batches (labelled by reporting period), defaulting to the active batch
 * (newest Open, else newest). The selection is held in `?batch=<id>` so reloads
 * and deep links reproduce the view (URL-is-state-of-record). Owns the loading
 * and no-batches states so each page stays a one-liner.
 *
 * `children` receives the resolved BatchDetail for the selected batch.
 */
export function BatchScopePicker({
  title,
  children,
}: {
  title: string;
  children: (batch: BatchDetail) => ReactNode;
}) {
  const [params, setParams] = useSearchParams();
  const listQ = useBatches();
  const active = useActiveBatchId();

  // Explicit ?batch= wins; otherwise default to the active batch without
  // dirtying the URL until the user actually changes the selection.
  const selectedId = params.get("batch") ?? active.batchId ?? null;
  const batchQ = useBatch(selectedId ?? undefined);

  const setSelected = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set("batch", id);
    else p.delete("batch");
    setParams(p, { replace: true });
  };

  const options =
    listQ.data?.items.map((b) => ({ value: b.batch_id, label: optionLabel(b) })) ?? [];

  return (
    <Stack gap="md">
      <Title order={2}>{title}</Title>
      {listQ.isPending || active.isPending ? (
        <Skeleton height={36} width={320} radius="sm" />
      ) : options.length > 0 ? (
        <Select
          label="Reporting period"
          data={options}
          value={selectedId}
          onChange={setSelected}
          allowDeselect={false}
          searchable
          maw={320}
        />
      ) : null}
      {listQ.isPending || active.isPending || batchQ.isPending ? (
        <Skeleton height={320} radius="md" />
      ) : batchQ.data ? (
        children(batchQ.data)
      ) : (
        <Alert color="gray" variant="light">
          <Text size="sm">No batches available yet.</Text>
        </Alert>
      )}
    </Stack>
  );
}
